import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { Contact } from "@/api/queries";
import { useStartConversation } from "./queries";
import { WhatsAppInvite } from "./WhatsAppInvite";

type ContactPhone = Contact["phones"][number];

/** `ContactPhone.Kind.LANDLINE`. A desk phone is not on WhatsApp. */
const LANDLINE = 2;

type Blocker = "arco" | "refused";

/**
 * Why this contact may not be written to at all, or `null`.
 *
 * Only a refusal blocks the button (D29). A contact with no answer on record
 * gets a thread like anyone else: the seller has already called them, and a
 * thread opened from our side can only start with an approved template.
 */
function blockerOf(contact: Contact): Blocker | null {
  if (contact.objected_at || contact.blocked_at) return "arco";
  if (contact.consents_to_whatsapp === false) return "refused";
  return null;
}

/**
 * *WhatsApp*, beside *Llamar* on a number: opens the thread with it in the
 * inbox. Nothing is sent from here. Inside the 24-hour window the agent types;
 * outside it the inbox offers the approved templates, the only first message
 * Meta accepts.
 */
export function WhatsAppButton({
  contact,
  phone,
}: {
  contact: Contact;
  phone: ContactPhone;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useStartConversation();

  if (phone.kind === LANDLINE) return null;
  const blocker = blockerOf(contact);
  // Nobody has asked yet: offer the way to ask that needs no template — the
  // client writes first, and that message is the consent.
  const mayInvite = blocker === null && contact.consents_to_whatsapp !== true;

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="btn btn-secondary px-3 py-1.5 text-sm"
        disabled={blocker !== null || start.isPending}
        title={blocker === null ? undefined : t(`whatsapp.${blocker}`)}
        onClick={() =>
          start.mutate(phone.id, {
            onSuccess: (conversation) =>
              void navigate(`/conversaciones?c=${conversation.id}`),
          })
        }
      >
        {start.isPending ? t("whatsapp.opening") : t("whatsapp.open")}
      </button>
      {blocker === null ? null : (
        <span className="text-[11px] text-mute-600" title={t(`whatsapp.${blocker}`)}>
          {t(`whatsapp.${blocker}Short`)}
        </span>
      )}
      {mayInvite ? <WhatsAppInvite contact={contact} /> : null}
      {start.isError ? (
        <span role="alert" className="text-xs text-brand-700">
          {start.error.message}
        </span>
      ) : null}
    </span>
  );
}
